@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.shared.billing.BillingClient
import com.royceecoleman.bill.shared.billing.BillingMatter
import com.royceecoleman.bill.shared.billing.BillingProfile
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** Saved clients, their timekeepers and matters, so each month's workspace starts with the right IDs and rates. */
@Composable
internal fun ClientsPage(
    clients: List<BillingClient>,
    profiles: List<BillingProfile>,
    busy: Boolean,
    markDirty: () -> Unit,
    save: (BillingClient, onSaved: () -> Unit) -> Unit,
    delete: (BillingClient) -> Unit,
    startWorkspace: (BillingClient, BillingMatter) -> Unit,
) {
    var editing by remember { mutableStateOf<BillingClient?>(null) }
    Eyebrow("CLIENTS & MATTERS")
    H1 { Text("Set up each client once.") }
    P({ classes("bill-muted") }) {
        Text("Client and matter IDs, timekeepers and agreed rates fill in automatically when you start a monthly workspace. You can still change them per invoice.")
    }
    val current = editing
    if (current != null) {
        ClientEditor(current, { editing ?: current }, profiles, busy, {
            editing = it
            markDirty()
        }, { save(editing ?: current) { editing = null } }, { editing = null })
        return
    }
    BillButton("New client", busy, "primary") { editing = BillingClient(matters = listOf(BillingMatter(id = "m1"))) }
    if (clients.isEmpty()) P({ classes("bill-card") }) { Text("No clients yet. Add one to reuse its IDs, timekeepers and rates every month.") }
    clients.forEach { client ->
        Div({ classes("bill-card") }) {
            Div({ classes("bill-row", "bill-between") }) {
                Div {
                    H3 { Text(client.name) }
                    Text("Client ID ${client.clientId.ifBlank { "—" }} · ${client.timekeepers.size} timekeepers · ${client.matters.size} matters")
                }
                Div({ classes("bill-row") }) {
                    BillButton("Edit", busy) { editing = client }
                    BillButton("Delete", busy, "danger") { delete(client) }
                }
            }
            client.matters.forEach { matter ->
                Div({ classes("bill-row", "bill-between") }) {
                    Text("${matter.name} · ${matter.matterId.ifBlank { "no firm matter ID" }}")
                    BillButton("Start a monthly workspace →", busy) { startWorkspace(client, matter) }
                }
            }
        }
    }
}

@Composable
private fun ClientEditor(
    client: BillingClient,
    latest: () -> BillingClient,
    profiles: List<BillingProfile>,
    busy: Boolean,
    change: (BillingClient) -> Unit,
    save: () -> Unit,
    cancel: () -> Unit,
) {
    val profileOptions = mapOf("" to "— None —") + profiles.associate { it.id to it.name }

    fun updateMatter(
        index: Int,
        matter: BillingMatter,
    ) = change(latest().copy(matters = latest().matters.mapIndexed { i, it -> if (i == index) matter else it }))
    Div({ classes("bill-card") }) {
        H2 { Text(if (client.id.isEmpty()) "New client" else "Edit ${client.name}") }
        Div({ classes("bill-grid") }) {
            BillField("Client name", client.name, "Acme Corp.") { change(latest().copy(name = it)) }
            BillField("Client ID (from your billing software)", client.clientId) { change(latest().copy(clientId = it.trim())) }
            BillField("Law firm ID / tax ID", client.firmId) { change(latest().copy(firmId = it.trim())) }
            BillField("Currency", client.currency) { change(latest().copy(currency = it.trim().uppercase())) }
            BillSelect("Default billing profile", client.defaultProfileId, profileOptions) { change(latest().copy(defaultProfileId = it)) }
        }
    }
    Div({ classes("bill-card") }) {
        H2 { Text("Timekeepers") }
        P({ classes("bill-muted") }) { Text("The first timekeeper is the default on new invoices; the others can be chosen per entry.") }
        TimekeepersEditor(client.timekeepers, { latest().timekeepers }) { change(latest().copy(timekeepers = it)) }
    }
    Div({ classes("bill-card") }) {
        H2 { Text("Matters") }
        client.matters.forEachIndexed { index, matter ->
            key(matter.id) {
                Div({ classes("bill-grid") }) {
                    BillField("Matter name", matter.name) { updateMatter(index, latest().matters[index].copy(name = it)) }
                    BillField("Firm matter ID", matter.matterId) { updateMatter(index, latest().matters[index].copy(matterId = it.trim())) }
                    BillField("Client matter ID", matter.clientMatterId) { updateMatter(index, latest().matters[index].copy(clientMatterId = it.trim())) }
                    BillField("Matter rate (optional)", matter.hourlyRate) { updateMatter(index, latest().matters[index].copy(hourlyRate = it.trim())) }
                    BillSelect("Billing profile", matter.profileId, profileOptions) { updateMatter(index, latest().matters[index].copy(profileId = it)) }
                    BillCheck("Client assigns no matter ID", matter.clientHasNoMatterId) { updateMatter(index, latest().matters[index].copy(clientHasNoMatterId = it)) }
                    BillButton("Remove matter", busy, "danger") { change(latest().copy(matters = latest().matters.filterIndexed { i, _ -> i != index })) }
                }
            }
        }
        BillButton("+ Add matter", busy || client.matters.size >= 100) {
            val used = latest().matters.map { it.id }.toSet()
            val id = generateSequence(latest().matters.size + 1) { it + 1 }.map { "m$it" }.first { it !in used }
            change(latest().copy(matters = latest().matters + BillingMatter(id = id)))
        }
    }
    Div({ classes("bill-row") }) {
        BillButton("Save client", busy, "primary", save)
        BillButton("Cancel", busy, "", cancel)
    }
}
